import { computed } from 'vue';

import { ElLoading } from 'element-plus';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import { ARRAY_MAX_LENGTH, HttpStatus } from '@/common/constants';
import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import { projectModule } from '@/features/project/store';
import yup from '@/plugins/yup';

import { projectPlanningService } from '../services/planning.service';
import { projectPlanningModule } from '../store';

const assignResourceGroupSchema = yup.object({
    selectedTaskRuleId: yup.string().nullable().required(),
    taskIds: yup
        .array()
        .of(yup.string().optional().optional().nullable())
        .max(ARRAY_MAX_LENGTH)
        .required(),
});

export const initAssignTaskRule = {
    selectedTaskRuleId: null,
    taskIds: [],
};

export const setupAssignTaskRule = () => {
    const { t } = useI18n();
    const { handleSubmit, errors, resetForm, validate, setErrors } = useForm({
        initialValues: initAssignTaskRule,
        validationSchema: assignResourceGroupSchema,
    });

    const onSubmit = handleSubmit(async (values) => {
        const loading = ElLoading.service({});
        const response = await projectPlanningService.assignTaskRule({
            selectedTaskRuleId: values.selectedTaskRuleId as string,
            taskIds: (values.taskIds as string[]) || [],
            projectId: projectModule.selectedProjectId || '',
        });
        loading.close();

        if (response.success) {
            showSuccessNotificationFunction(
                t('planning.assignResourceGroup.message.assignResourceGroupSuccess'),
            );
            projectPlanningModule.setIsShowAssignTaskRulePopup(false);
        } else {
            if (response.code === HttpStatus.ITEM_NOT_FOUND) {
                projectPlanningModule.setIsShowAssignResourceGroupForm(false);
                projectPlanningModule.setHasTaskDeleted(true);
            }
            showErrorNotificationFunction(response.message);
        }
    });

    const { value: selectedTaskRuleId } = useField('selectedTaskRuleId');
    const { value: taskIds } = useField('taskIds');

    const openTaskRuleForm = async () => {
        setErrors({});
        const selectedTask = computed(() => projectPlanningModule.selectedTaskIdList);
        // editing mode
        if (selectedTask.value.length === 1) {
            const loading = ElLoading.service({});
            const taskResponse = await projectPlanningService.getTask(
                selectedTask.value[0],
            );
            loading.close();
            if (taskResponse.success) {
                resetForm({
                    values: {
                        ...initAssignTaskRule,
                        selectedTaskRuleId: taskResponse.data.rules,
                    },
                });
            }
        } else {
            resetForm({
                values: {
                    ...initAssignTaskRule,
                },
            });
        }
    };

    return {
        errors,
        onSubmit,
        resetForm,
        setErrors,
        validate,
        openTaskRuleForm,
        selectedTaskRuleId,
        taskIds,
    };
};
